import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import {
  ClauseScannerAnalysisType,
  DocumentTypes,
  EvaluationTypeLabelsEnum,
} from '@/app/utils/constants';
import { ContractsIcon, Refresh01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { ClausesPageContent } from '@/app/components/features/evaluations/Clauses/ClausesPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSetAtom } from 'jotai';
import { rerunClauseAnalysisOpenAtom } from '@/app/components/features/evaluations/Clauses/store';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/clauses/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tGeneral = useTranslations('general');
  const tFeature = useTranslations('features.clauses');
  const { contractId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const setRerunAnalysisOpen = useSetAtom(rerunClauseAnalysisOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={contractId}
          documentType={DocumentTypes.CONTRACT}
          onClick={() => setRerunAnalysisOpen(true)}
          aria-label={tGeneral('reRunAnalysis')}
        >
          <HugeiconsIcon icon={Refresh01Icon} />
        </RunAnalysisHeaderButton>
      </Header>
      <ClausesPageContent
        clauseScannerType={ClauseScannerAnalysisType.CONTRACT}
        resourceId={contractId}
      />
    </>
  );
}
